import axios from 'axios';
import { Actions } from 'vuex-smart-module';
import { Filters, FiltersPagination } from '@/types';
import { ResponseStatuses } from '@/enums';
import CoursesRequestsState from './state';
import CoursesRequestsMutations from './mutations';
import CoursesRequestsGetters from './getters';
import { CoursesRequestsService } from '../services';

const service = new CoursesRequestsService();

export default class CoursesRequestsActions extends Actions<
  CoursesRequestsState,
  CoursesRequestsGetters,
  CoursesRequestsMutations,
  // eslint-disable-next-line no-use-before-define
  CoursesRequestsActions
> {
  async get_courses_requests() {
    try {
      this.mutations.TOGGLE_COURSES_REQUESTS_LOADING(true);
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await service.get_courses_requests(payload);

      this.mutations.SET_COURSES_REQUESTS(data.items);
      this.mutations.SET_COURSES_REQUEST_STATUSES(data.statuses);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_COURSES_REQUESTS_LOADING(false);
    }
  }

  async delete_courses_request(id: string | number) {
    try {
      await service.delete_courses_request(id);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async get_courses_request(id: number) {
    try {
      this.mutations.TOGGLE_COURSES_REQUESTS_LOADING(true);

      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await service.get_courses_request(id);

      this.mutations.SET_COURSES_REQUEST(data);

      return code;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.request?.status;
      }
    } finally {
      this.mutations.TOGGLE_COURSES_REQUESTS_LOADING(false);
    }
  }

  // ----------------------------------------Courses----------------------------------------

  async get_courses(request_id: number) {
    try {
      this.mutations.TOGGLE_COURSES_LOADING(true);

      const { page, itemsPerPage } = this.state.courses.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: {},
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      const { data, meta } = await service.get_courses(payload, request_id);

      this.mutations.SET_COURSES({ request_id, items: data.items });

      this.mutations.UPD_COURSES_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_COURSES_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_COURSES_PAGINATION_FIELDS(meta);
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_COURSES_LOADING(false);
    }
  }

  // ----------------------------------------Staffs----------------------------------------

  async get_requested_staffs(request_id: number) {
    try {
      this.mutations.TOGGLE_STAFFS_LOADING(true);

      const { page, itemsPerPage } = this.state.staffs.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.staffs.filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      const filters_exist = this.state.staffs.table_filters.staffs.length > 0
        && this.state.staffs.table_filters.courses.length > 0;

      const { data, meta } = await service.get_staffs(
        payload,
        request_id,
        filters_exist,
      );

      this.mutations.SET_STAFFS({
        request_id,
        items: data.items,
        filters: !filters_exist ? data.filters : { courses: [], staffs: [] },
      });
      this.mutations.UPD_STAFFS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_STAFFS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_STAFFS_PAGINATION_FIELDS(meta);
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_STAFFS_LOADING(false);
    }
  }

  // ----------------------------------------History----------------------------------------

  async get_history(request_id: number) {
    try {
      this.mutations.TOGGLE_HISTORY_LOADING(true);

      const { page, itemsPerPage } = this.state.history.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: {},
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      const { data, meta } = await service.get_history(payload, request_id);

      this.mutations.SET_HISTORY({ request_id, items: data.items });

      this.mutations.UPD_HISTORY_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_HISTORY_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_HISTORY_PAGINATION_FIELDS(meta);
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_HISTORY_LOADING(false);
    }
  }
}
